import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Empty, Segmented, Spin, Tag } from "antd";
import { CheckCircleOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import ApiService from "../../core/service/api-service";
import { dateFormat } from "../../core/utils/date-utils";
import ReportPreview from "./ReportPreview";
import ReportVersionPreview from "./ReportVersionPreview";
import { REPORT_FIELD_KEYS, REPORT_SECTIONS, reportFieldValue } from "./report-field-keys";
import { diffSection } from "./report-diff";
import "./report-versions.css";

const CURRENT = 'current';

/**
 * "Rapor Versiyonları": solda zaman çizelgesi (en üstte raporun güncel hali, altında arşivlenmiş sürümler
 * yeniden eskiye), sağda karşılaştırma.
 *
 * Karşılaştırma çifti: satıra tıklanınca o giriş ile bir altındaki (bir önceki) giriş; iki kutucuk
 * işaretliyse o ikisi. Her zaman eski → yeni yönünde gösterilir.
 *
 * Sürüm satırı, düzenlemeden ÖNCEKİ içeriğin arşividir (tarih/kullanıcı o içeriğe aittir); raporun güncel
 * içeriği sürümlerde değil raporun kendisindedir — bu yüzden "Güncel hal" ayrı bir giriş olarak eklenir.
 * Adı geçmişten kalma: artık grid değil, çağıranlar değişmesin diye aynı bileşen.
 */
export default function ReportVersionsGrid({ studyId, requestId, reportId })
{
    const { t } = useTranslation();
    const [report, setReport] = useState(null);
    const [versions, setVersions] = useState(null);
    const [focusId, setFocusId] = useState(CURRENT);
    const [checked, setChecked] = useState([]);
    const [contents, setContents] = useState({});
    const [view, setView] = useState('changes');
    const [layout, setLayout] = useState('unified');

    useEffect(() => {
        (async () => {
            const [reportRes, versionsRes] = await Promise.all([
                ApiService.Report.Get(studyId, requestId, reportId),
                ApiService.Report.GetReportVersions(studyId, reportId, { paginationQuery: { pageNumber: 1, pageSize: 500 } }),
            ]);
            if (reportRes.success)
                setReport(reportRes.data);
            setVersions(versionsRes.success ? (versionsRes.data || []) : []);
        })();
    }, [studyId, requestId, reportId]);

    const entries = useMemo(() => {
        if (!report || !versions)
            return [];

        return [
            {
                id: CURRENT,
                isCurrent: true,
                date: report.reportFileModifiedDate,
                user: report.modifiedUser?.visibleName,
                reader: report.studyReadUser?.user?.visibleName,
                isConfirm: report.isConfirm,
            },
            ...versions.map(v => ({
                id: v.id,
                date: v.createdDate,
                user: v.creatorUser?.visibleName,
                reader: v.studyReadUser?.visibleName,
                isAutoSave: v.isAutoSave,
            })),
        ];
    }, [report, versions]);

    const indexOf = (id) => entries.findIndex(e => e.id === id);

    // Listede küçük indeks = daha yeni.
    const pair = useMemo(() => {
        if (entries.length === 0)
            return { newer: null, older: null };

        if (checked.length === 2) {
            const [a, b] = [...checked].sort((x, y) => indexOf(x) - indexOf(y));
            return { newer: entries[indexOf(a)], older: entries[indexOf(b)] };
        }

        const i = Math.max(0, indexOf(focusId));
        return { newer: entries[i], older: entries[i + 1] ?? null };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries, checked, focusId]);

    // İçerik yalnız karşılaştırılan girişler için ve bir kez çekilir.
    useEffect(() => {
        [pair.newer, pair.older].filter(Boolean).forEach(async (entry) => {
            if (contents[entry.id])
                return;

            setContents(prev => ({ ...prev, [entry.id]: { loading: true } }));
            const res = entry.isCurrent
                ? await ApiService.ReportGenerator.GetReportModel(studyId, requestId, reportId)
                : await ApiService.ReportGenerator.GetReportVersionPreview(studyId, entry.id);

            setContents(prev => ({
                ...prev,
                [entry.id]: res?.success ? { fields: res.data?.reportFields || [] } : { error: true },
            }));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pair]);

    const toggleChecked = (id, on) => setChecked(prev => {
        if (!on)
            return prev.filter(x => x !== id);
        // En fazla iki: üçüncü işaretlenince en eskisi düşer.
        return [...prev.filter(x => x !== id), id].slice(-2);
    });

    const headerFields = contents[CURRENT]?.fields ?? contents[pair.newer?.id]?.fields;

    if (!report || !versions)
        return <div className="rv-loading"><Spin /></div>;

    return (
        <div className="rv-root">
            {headerFields && <PatientHeader fields={headerFields} />}

            <div className="rv-body">
                <aside className="rv-timeline">
                    <div className="rv-hint">{t('RV_COMPARE_HINT')}</div>
                    {versions.length === 0 && <div className="rv-hint">{t('RV_NO_VERSIONS')}</div>}
                    {entries.map(entry => (
                        <TimelineItem
                            key={entry.id}
                            entry={entry}
                            side={entry === pair.newer ? 'newer' : entry === pair.older ? 'older' : null}
                            checked={checked.includes(entry.id)}
                            onCheck={(on) => toggleChecked(entry.id, on)}
                            onClick={() => {
                                setChecked([]);
                                setFocusId(entry.id);
                            }}
                        />
                    ))}
                    {checked.length > 0 && (
                        <Button size="small" type="link" onClick={() => setChecked([])}>
                            {t('RV_CLEAR_SELECTION')}
                        </Button>
                    )}
                </aside>

                <section className="rv-compare">
                    <div className="rv-toolbar">
                        <Segmented
                            size="small"
                            value={view}
                            onChange={setView}
                            options={[
                                { value: 'changes', label: t('RV_CHANGES') },
                                { value: 'preview', label: t('RV_FULL_PREVIEW') },
                            ]}
                        />
                        {view === 'changes' && pair.older && (
                            <Segmented
                                size="small"
                                value={layout}
                                onChange={setLayout}
                                options={[
                                    { value: 'unified', label: t('RV_UNIFIED') },
                                    { value: 'split', label: t('RV_SIDE_BY_SIDE') },
                                ]}
                            />
                        )}
                    </div>

                    {view === 'changes'
                        ? <ChangesView pair={pair} contents={contents} layout={layout} />
                        : (
                            <div className="rv-preview">
                                {pair.newer?.isCurrent
                                    ? <ReportPreview reportId={reportId} studyId={studyId} requestId={requestId} disableActions={true} />
                                    : <ReportVersionPreview report={report} versionId={pair.newer?.id} studyId={studyId} />}
                            </div>
                        )}
                </section>
            </div>
        </div>
    );
}

function PatientHeader({ fields })
{
    const { t } = useTranslation();
    const value = (key) => reportFieldValue(fields, key);
    const items = [
        [t('Patient Name'), value(REPORT_FIELD_KEYS.patientName)],
        [t('Patient Id'), value(REPORT_FIELD_KEYS.patientId)],
        [t('Accession Number'), value(REPORT_FIELD_KEYS.accessionNumber)],
        [t('Study Date'), value(REPORT_FIELD_KEYS.studyDate)],
    ].filter(([, v]) => v);

    return (
        <div className="rv-patient">
            <strong className="rv-patient-request">{value(REPORT_FIELD_KEYS.requestName)}</strong>
            {items.map(([label, v]) => (
                <span key={label}><span className="rv-muted">{label}:</span> {v}</span>
            ))}
        </div>
    );
}

function TimelineItem({ entry, side, checked, onCheck, onClick })
{
    const { t } = useTranslation();

    return (
        <div
            role="button"
            tabIndex={0}
            className={`rv-item${side ? ` is-${side}` : ''}`}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            {/* Kutucuk satırın tıklamasını (tek sürüm seçimi) tetiklemesin. */}
            <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <Checkbox checked={checked} onChange={(e) => onCheck(e.target.checked)} />
            </span>
            <div className="rv-item-main">
                <div className="rv-item-title">
                    <span>{entry.isCurrent ? t('RV_CURRENT') : dateFormat(entry.date)}</span>
                    {entry.isCurrent && (entry.isConfirm
                        ? <Tag color="green" icon={<CheckCircleOutlined />}>{t('RV_CONFIRMED')}</Tag>
                        : <Tag icon={<EditOutlined />}>{t('RV_NOT_CONFIRMED')}</Tag>)}
                    {entry.isAutoSave && <Tag icon={<SaveOutlined />}>{t('RV_AUTO_SAVE')}</Tag>}
                </div>
                <div className="rv-item-meta">
                    {entry.isCurrent && <span>{dateFormat(entry.date)}</span>}
                    {entry.user && <span>{entry.user}</span>}
                    {entry.reader && <span>{t('RV_READ_BY', { name: entry.reader })}</span>}
                </div>
            </div>
        </div>
    );
}

function ChangesView({ pair, contents, layout })
{
    const { t } = useTranslation();
    const newer = contents[pair.newer?.id];
    const older = pair.older ? contents[pair.older.id] : null;

    if (!newer || newer.loading || (older && older.loading))
        return <div className="rv-loading"><Spin /></div>;

    if (newer.error || older?.error)
        return <Empty description={t('RV_LOAD_FAILED')} />;

    const label = (entry) => (entry.isCurrent ? t('RV_CURRENT') : `${dateFormat(entry.date)}${entry.user ? ` · ${entry.user}` : ''}`);

    return (
        <div className="rv-changes">
            <div className="rv-pair">
                {pair.older
                    ? (
                        <>
                            <span className="rv-pair-older">{label(pair.older)}</span>
                            <span className="rv-muted">→</span>
                            <span className="rv-pair-newer">{label(pair.newer)}</span>
                            <span className="rv-legend">
                                <ins className="rv-ins">{t('RV_ADDED')}</ins>
                                <del className="rv-del">{t('RV_REMOVED')}</del>
                            </span>
                        </>
                    )
                    : <span className="rv-muted">{t('RV_FIRST_VERSION')}</span>}
            </div>

            {REPORT_SECTIONS.map(section => (
                <SectionDiff
                    key={section.key}
                    title={t(section.label)}
                    newerHtml={reportFieldValue(newer.fields, section.key)}
                    olderHtml={older ? reportFieldValue(older.fields, section.key) : null}
                    layout={layout}
                />
            ))}
        </div>
    );
}

function SectionDiff({ title, newerHtml, olderHtml, layout })
{
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    // Önceki giriş yoksa (ilk sürüm) bölüm düz metin gösterilir.
    const diff = olderHtml === null
        ? { ...diffSection(newerHtml, newerHtml), status: 'first' }
        : diffSection(olderHtml, newerHtml);

    const unchanged = diff.status === 'same' || diff.status === 'format';
    const empty = !diff.text && diff.status !== 'changed';

    return (
        <div className={`rv-section${diff.status === 'changed' ? ' is-changed' : ''}`}>
            <div className="rv-section-title">
                <span>{title}</span>
                {diff.status === 'same' && <span className="rv-section-note">{t('RV_NO_CHANGE')}</span>}
                {diff.status === 'format' && <span className="rv-section-note">{t('RV_FORMAT_ONLY')}</span>}
                {unchanged && !empty && (
                    <Button size="small" type="link" onClick={() => setExpanded(x => !x)}>
                        {expanded ? t('RV_HIDE') : t('RV_SHOW')}
                    </Button>
                )}
            </div>

            {diff.status === 'first' && (empty ? <div className="rv-muted">—</div> : <div className="rv-text">{diff.text}</div>)}

            {unchanged && diff.status !== 'first' && expanded && <div className="rv-text rv-text-muted">{diff.text}</div>}

            {diff.status === 'changed' && (layout === 'split'
                ? (
                    <div className="rv-split">
                        <div className="rv-text">
                            {diff.parts.filter(p => !p.added).map((p, i) => (p.removed
                                ? <del key={i} className="rv-del">{p.value}</del>
                                : <span key={i}>{p.value}</span>))}
                        </div>
                        <div className="rv-text">
                            {diff.parts.filter(p => !p.removed).map((p, i) => (p.added
                                ? <ins key={i} className="rv-ins">{p.value}</ins>
                                : <span key={i}>{p.value}</span>))}
                        </div>
                    </div>
                )
                : (
                    <div className="rv-text">
                        {diff.parts.map((p, i) => {
                            if (p.added)
                                return <ins key={i} className="rv-ins">{p.value}</ins>;
                            if (p.removed)
                                return <del key={i} className="rv-del">{p.value}</del>;
                            return <span key={i}>{p.value}</span>;
                        })}
                    </div>
                ))}
        </div>
    );
}
